import React, { useState, useRef, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';

import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { PLACEHOLDER, INPUT_LABEL } from '../../utils/constant/placeHolders';
import { GLOBAL } from '../../utils/constant/global';

const { BTN_PLEASE_WAIT, BTN_LOGIN_TITLE } = BT_BUTTON_LABELS;
const { LOADING } = GLOBAL;

/**
 * SignInCard — presentational sign-in form card.
 *
 * All auth logic stays in the parent (SignIn). This component only renders
 * the UI and forwards events through the props it receives, so behavior is
 * unchanged.
 */
function SignInCard({
  register,
  errors,
  onSubmit,
  handleSignInWithGoogle,
  loading,
  disableSubmit,
  showPassword,
  setShowPassword
}) {
  // Local, presentation-only state — does not affect auth behavior.
  const [capsLockOn, setCapsLockOn] = useState(false);
  const googleBtnRef = useRef(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(376);

  useEffect(() => {
    const node = googleBtnRef.current;
    if (!node) return undefined;

    const syncWidth = () => {
      const width = Math.floor(node.getBoundingClientRect().width);
      if (width > 0) setGoogleBtnWidth(width);
    };

    syncWidth();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handlePasswordKey = (e) => {
    // getModifierState is supported in all evergreen browsers.
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  return (
    <div className="auth-signin__glass">
      <header className="auth-signin__head">
        <h1 className="auth-signin__title">Welcome to HRX</h1>
        <p className="auth-signin__subtitle">Sign in to continue to Software Co HRX</p>
      </header>

      <form className="auth-signin__form" onSubmit={onSubmit} noValidate>
        <div className="auth-signin__field">
          <label className="auth-signin__label" htmlFor="auth-emailMobile">
            {INPUT_LABEL.LB_EMAIL || 'Email'}
          </label>
          <div className={`auth-signin__input ${errors.emailMobile ? 'is-error' : ''}`}>
            <span className="auth-signin__icon" aria-hidden>
              <i className="icofont icofont-email" />
            </span>
            <input
              id="auth-emailMobile"
              type="text"
              autoComplete="username"
              {...register('emailMobile')}
              placeholder={PLACEHOLDER.PH_EMAIL_INPUT || 'Enter Email'}
              aria-invalid={errors.emailMobile ? 'true' : 'false'}
            />
          </div>
          {errors?.emailMobile?.message ? (
            <small className="auth-signin__error" role="alert">
              <i className="icofont icofont-warning-alt" aria-hidden />
              {errors.emailMobile.message}
            </small>
          ) : null}
        </div>

        <div className="auth-signin__field">
          <label className="auth-signin__label" htmlFor="auth-password">
            Password
          </label>
          <div className={`auth-signin__input ${errors.password ? 'is-error' : ''}`}>
            <span className="auth-signin__icon" aria-hidden>
              <i className="icofont icofont-lock" />
            </span>
            <input
              id="auth-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...register('password')}
              placeholder="Password"
              aria-invalid={errors.password ? 'true' : 'false'}
              onKeyUp={handlePasswordKey}
              onKeyDown={handlePasswordKey}
            />
            <button
              type="button"
              className="auth-signin__togglePassword"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
              disabled={loading}
            >
              <i
                className={`icofont ${showPassword ? 'icofont-eye-blocked' : 'icofont-eye'}`}
                aria-hidden
              />
            </button>
          </div>
          {capsLockOn ? (
            <small className="auth-signin__hint" role="status">
              <i className="icofont icofont-warning-alt" aria-hidden />
              Caps Lock is on
            </small>
          ) : null}
          {errors?.password?.message ? (
            <small className="auth-signin__error" role="alert">
              <i className="icofont icofont-warning-alt" aria-hidden />
              {errors.password.message}
            </small>
          ) : null}
        </div>

        <button type="submit" className="auth-signin__submit" disabled={disableSubmit}>
          {loading ? BTN_PLEASE_WAIT : BTN_LOGIN_TITLE}
          {loading ? (
            <span className="auth-signin__spinner" aria-hidden>
              <span className="spinner-border spinner-border-sm" role="status" />
            </span>
          ) : (
            <span className="auth-signin__submitArrow" aria-hidden>
              <i className="icofont icofont-long-arrow-right" />
            </span>
          )}
        </button>

        {loading ? (
          <div className="auth-signin__loading" role="status" aria-live="polite">
            <span className="visually-hidden">{LOADING}</span>
          </div>
        ) : null}

        <div className="auth-signin__oauth">
          <div className="auth-signin__divider" aria-hidden>
            <span>or</span>
          </div>
          <div className="auth-signin__google">
            <div
              className={`auth-signin__googleBtn${loading ? ' is-loading' : ''}`}
              ref={googleBtnRef}
            >
              <div className="auth-signin__googleSurface" aria-hidden="true">
                <svg
                  className="auth-signin__googleMark"
                  viewBox="0 0 48 48"
                  xmlns="http://www.w3.org/2000/svg"
                  focusable="false"
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.56 2.94-2.23 5.43-4.74 7.08l7.19 5.58C43.98 37.13 46.98 31.3 46.98 24.55z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.19-5.58c-2.01 1.35-4.59 2.16-8.7 2.16-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
                <span className="auth-signin__googleLabel">Continue with Google</span>
              </div>
              <div className="auth-signin__googleNative">
                <GoogleLogin
                  onSuccess={handleSignInWithGoogle}
                  onError={handleSignInWithGoogle}
                  text="continue_with"
                  shape="rectangular"
                  size="large"
                  theme="outline"
                  logo_alignment="left"
                  width={googleBtnWidth}
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      <p className="auth-signin__footer">
        © {new Date().getFullYear()} Software Co · Secure HRX access
      </p>
    </div>
  );
}

export default SignInCard;
